import { Component } from "@/types";
import type { BaseChartProperties } from "@/types/chart-properties";
import { ChartMixinBase, MixinResult } from "./ChartMixinBase";

/**
 * Mixin for handling axis-related commands for cartesian charts
 */
export class AxisMixin extends ChartMixinBase {
  parseCommand(command: string, component: Component): MixinResult {
    const changes: Partial<BaseChartProperties> = {};

    // X-axis label
    const xLabel = this.extractAxisLabel(command, 'x');
    if (xLabel) {
      changes.xAxisLabel = xLabel;
    }

    // Y-axis label
    const yLabel = this.extractAxisLabel(command, 'y');
    if (yLabel) {
      changes.yAxisLabel = yLabel;
    }

    // Axis visibility
    // Check for both axes being removed together - include hyphenated variants
    if (this.hasKeywords(command, [
      'remove the x and y axis', 'hide both axes', 'no axes', 'remove axes',
      'remove the x-axis and y-axis', 'hide both axis', 'remove both axes'
    ])) {
      changes.showXAxis = false;
      changes.showYAxis = false;
    } else if (this.hasKeywords(command, [
      'show both axes', 'display axes', 'add axes',
      'show both axis', 'display both axes'
    ])) {
      changes.showXAxis = true;
      changes.showYAxis = true;
    } else {
      // Individual axis visibility - include hyphenated variants
      if (this.hasKeywords(command, [
        'hide x axis', 'remove x axis', 'no x axis', 'remove the x axis',
        'hide x-axis', 'remove x-axis', 'no x-axis', 'remove the x-axis'
      ])) {
        changes.showXAxis = false;
      }
      if (this.hasKeywords(command, [
        'show x axis', 'display x axis', 'add x axis',
        'show x-axis', 'display x-axis', 'add x-axis'
      ])) {
        changes.showXAxis = true;
      }
      if (this.hasKeywords(command, [
        'hide y axis', 'remove y axis', 'no y axis', 'remove the y axis',
        'hide y-axis', 'remove y-axis', 'no y-axis', 'remove the y-axis'
      ])) {
        changes.showYAxis = false;
      }
      if (this.hasKeywords(command, [
        'show y axis', 'display y axis', 'add y axis',
        'show y-axis', 'display y-axis', 'add y-axis'
      ])) {
        changes.showYAxis = true;
      }
    }

    // Axis scale
    const scaleChanges = this.parseAxisScale(command);
    if (scaleChanges) {
      Object.assign(changes, scaleChanges);
    }

    // Axis range
    const rangeChanges = this.parseAxisRange(command, component);
    if (rangeChanges) {
      Object.assign(changes, rangeChanges);
    }

    // Tick formatting
    const tickChanges = this.parseTickFormatting(command);
    if (tickChanges) {
      Object.assign(changes, tickChanges);
    }

    // Grid lines specific to axes
    if (this.hasKeywords(command, ['x grid', 'vertical grid'])) {
      changes.xGrid = command.includes('hide') || command.includes('remove') ? false : true;
    }
    if (this.hasKeywords(command, ['y grid', 'horizontal grid'])) {
      changes.yGrid = command.includes('hide') || command.includes('remove') ? false : true;
    }

    // Axis rotation
    const rotationAngle = this.parseAxisRotation(command);
    if (rotationAngle !== null) {
      changes.xAxisAngle = rotationAngle;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private extractAxisLabel(command: string, axis: 'x' | 'y'): string | null {
    const patterns = [
      new RegExp(`${axis}[\\s-]?axis (?:label )?(?:to |:|=)?["']([^"']+)["']`, 'i'),
      new RegExp(`(?:set |change )?${axis} (?:label )?(?:to |:|=)?["']([^"']+)["']`, 'i'),
      new RegExp(`label ${axis}[\\s-]?axis ["']([^"']+)["']`, 'i')
    ];

    for (const pattern of patterns) {
      const match = command.match(pattern);
      if (match) {
        return match[1];
      }
    }

    return null;
  }

  private parseAxisScale(command: string): MixinResult {
    const changes: Partial<BaseChartProperties> = {};

    // Logarithmic scale
    if (this.hasKeywords(command, ['logarithmic', 'log scale'])) {
      if (command.includes('x')) {
        changes.xScale = 'log';
      }
      if (command.includes('y')) {
        changes.yScale = 'log';
      }
      if (!command.includes('x') && !command.includes('y')) {
        changes.yScale = 'log'; // Default to y-axis
      }
    }

    // Linear scale
    if (this.hasKeywords(command, ['linear scale', 'normal scale'])) {
      if (command.includes('x')) {
        changes.xScale = 'linear';
      }
      if (command.includes('y')) {
        changes.yScale = 'linear';
      }
      if (!command.includes('x') && !command.includes('y')) {
        changes.yScale = 'linear';
      }
    }

    // Time scale
    if (this.hasKeywords(command, ['time scale', 'date scale', 'temporal'])) {
      if (command.includes('x')) {
        changes.xScale = 'time';
      }
      if (command.includes('y')) {
        changes.yScale = 'time';
      }
      if (!command.includes('x') && !command.includes('y')) {
        changes.xScale = 'time'; // Default to x-axis for time
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private parseAxisRange(command: string, component: Component): MixinResult {
    const changes: Partial<BaseChartProperties> = {};

    // X-axis range
    const xRangeMatch = command.match(/x[\\s-]?(?:axis )?(?:from|range|between)\s+(-?\d+(?:\.\d+)?)\s+(?:to|and|-)\s+(-?\d+(?:\.\d+)?)/i);
    if (xRangeMatch) {
      changes.xDomain = [parseFloat(xRangeMatch[1]), parseFloat(xRangeMatch[2])];
    }

    // Y-axis range
    const yRangeMatch = command.match(/y[\\s-]?(?:axis )?(?:from|range|between)\s+(-?\d+(?:\.\d+)?)\s+(?:to|and|-)\s+(-?\d+(?:\.\d+)?)/i);
    if (yRangeMatch) {
      changes.yDomain = [parseFloat(yRangeMatch[1]), parseFloat(yRangeMatch[2])];
    }

    // Min/max values
    const xMinMatch = command.match(/x[\\s-]?(?:axis )?min(?:imum)?\s+(-?\d+(?:\.\d+)?)/i);
    if (xMinMatch) {
      const xDomainRaw = component.properties?.xDomain;
      const currentMax = Array.isArray(xDomainRaw) && typeof xDomainRaw[1] === 'number' ? xDomainRaw[1] : 100;
      changes.xDomain = [parseFloat(xMinMatch[1]), currentMax];
    }

    const xMaxMatch = command.match(/x[\\s-]?(?:axis )?max(?:imum)?\s+(-?\d+(?:\.\d+)?)/i);
    if (xMaxMatch) {
      const xDomainRaw = component.properties?.xDomain;
      const currentMin = Array.isArray(xDomainRaw) && typeof xDomainRaw[0] === 'number' ? xDomainRaw[0] : 0;
      changes.xDomain = [currentMin, parseFloat(xMaxMatch[1])];
    }

    const yMinMatch = command.match(/y[\\s-]?(?:axis )?min(?:imum)?\s+(-?\d+(?:\.\d+)?)/i);
    if (yMinMatch) {
      const yDomainRaw = component.properties?.yDomain;
      const currentMax = Array.isArray(yDomainRaw) && typeof yDomainRaw[1] === 'number' ? yDomainRaw[1] : 100;
      changes.yDomain = [parseFloat(yMinMatch[1]), currentMax];
    }

    const yMaxMatch = command.match(/y[\\s-]?(?:axis )?max(?:imum)?\s+(-?\d+(?:\.\d+)?)/i);
    if (yMaxMatch) {
      const yDomainRaw = component.properties?.yDomain;
      const currentMin = Array.isArray(yDomainRaw) && typeof yDomainRaw[0] === 'number' ? yDomainRaw[0] : 0;
      changes.yDomain = [currentMin, parseFloat(yMaxMatch[1])];
    }

    // Auto scale
    if (this.hasKeywords(command, ['auto scale', 'auto range', 'fit data'])) {
      changes.autoScale = true;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private parseTickFormatting(command: string): MixinResult {
    const changes: Partial<BaseChartProperties> = {};

    // Number of ticks
    const tickCountMatch = command.match(/(?:show |display |use )?(\d+)\s+ticks?/i);
    if (tickCountMatch) {
      const count = parseInt(tickCountMatch[1]);
      if (command.includes('x')) {
        changes.xTickCount = count;
      } else if (command.includes('y')) {
        changes.yTickCount = count;
      } else {
        changes.xTickCount = count;
        changes.yTickCount = count;
      }
    }

    // Tick formatting
    if (this.hasKeywords(command, ['percentage', 'percent', '%'])) {
      if (command.includes('x')) {
        changes.xTickFormat = 'percentage';
      } else if (command.includes('y')) {
        changes.yTickFormat = 'percentage';
      } else {
        changes.yTickFormat = 'percentage'; // Default to y-axis
      }
    }

    if (this.hasKeywords(command, ['currency', 'dollar', '$'])) {
      if (command.includes('x')) {
        changes.xTickFormat = 'currency';
      } else if (command.includes('y')) {
        changes.yTickFormat = 'currency';
      } else {
        changes.yTickFormat = 'currency';
      }
    }

    if (this.hasKeywords(command, ['thousand', 'k suffix', 'abbreviate'])) {
      if (command.includes('x')) {
        changes.xTickFormat = 'abbreviated';
      } else if (command.includes('y')) {
        changes.yTickFormat = 'abbreviated';
      } else {
        changes.yTickFormat = 'abbreviated';
      }
    }

    // Custom format string
    const formatMatch = command.match(/(?:tick )?format ["']([^"']+)["']/i);
    if (formatMatch) {
      if (command.includes('x')) {
        changes.xTickFormat = formatMatch[1];
      } else if (command.includes('y')) {
        changes.yTickFormat = formatMatch[1];
      } else {
        changes.yTickFormat = formatMatch[1];
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private parseAxisRotation(command: string): number | null {
    // Check for specific angle
    const angleMatch = command.match(/rotate\s+(?:x[\\s-]?axis\s+)?(?:labels?\s+)?(?:by\s+)?(-?\d+)/i);
    if (angleMatch) {
      return parseInt(angleMatch[1]);
    }

    // Check for common rotations
    if (this.hasKeywords(command, ['rotate labels', 'diagonal labels', 'angled labels'])) {
      return -45; // Common default rotation
    }

    if (this.hasKeywords(command, ['vertical labels', 'perpendicular'])) {
      return -90;
    }

    if (this.hasKeywords(command, ['horizontal labels', 'straight labels'])) {
      return 0;
    }

    return null;
  }

  getSuggestions(component: Component): string[] {
    const suggestions: string[] = [];

    if (!component.properties?.xAxisLabel) {
      suggestions.push('Set x-axis label to "Month"');
    }
    if (!component.properties?.yAxisLabel) {
      suggestions.push('Set y-axis label to "Sales"');
    }

    suggestions.push(
      "Use logarithmic scale for y-axis",
      "Set y-axis range from 0 to 1000",
      "Format y-axis as currency",
      "Rotate x-axis labels by -45 degrees",
      "Show 10 ticks on x-axis"
    );

    return suggestions;
  }
}

export const axisMixin = new AxisMixin();
